import { useMutation } from 'react-query';

import { useNavigate } from 'react-router-dom';
import {
  ILoginDetails,
  ILoginResponse,
  IServerErrorResponse,
} from '../../types';
import { httpService } from '../../services/http.service';
import { BASE_URL, USER } from '../../constants/endpoints';
import { useStore } from '../../store';
import { getClientId } from '../../helpers';
import { VIEWS_MAP } from '../../router/constants';
import { generateCaptchaToken } from '../../services/grecaptcha.service';
import {
  identifyMixPanelUser,
  trackMixPanel,
} from '../../services/mixpanel.service';
import { removeFromLocalStorage, setToLocalStorage } from '../../services/storage.service';

const {
  BASE,
  CONTROLLERS: {
    SECURITY: {
      BASE: SECURITY_BASE,
      ROUTES: {
        LOGIN: { BASE: LOGIN_BASE },
      },
    },
  },
} = USER;

const loginURL = `${BASE_URL}/${BASE.replace(
  '{version}',
  'v2',
)}/${SECURITY_BASE}/${LOGIN_BASE}`;

const sentToLoginErrorCodes = [401];

export const useLoginMutation = (
  loginDetails: ILoginDetails,
  onSuccess: (data: ILoginResponse) => void,
) => {
  const navigate = useNavigate();
  const setUser = useStore((state) => state.setUser);
  const personExternalId = useStore(state => state.getPersonExternalId());
  loginDetails.clientId = getClientId() ?? '';
  const setTokenStatus = useStore((state) => state.setTokenStatus);

  return useMutation(
    async () => {
      const captchaToken = await generateCaptchaToken();
      const data = await httpService.post(loginURL, loginDetails, {
        headers: {
          recaptcha: captchaToken as string,
        },
      });
      return data;
    },
    {
      onSuccess: async (res: { data: ILoginResponse }) => {
        /* clear previous AuthId (if exist) */
        removeFromLocalStorage('authUserId');

        let { data } = res;
        data = { ...data, initialPassword: loginDetails.password };
        setUser(data);
        onSuccess(data);

        /* If there is AuthUserId - save it to localStorage */
        if (data.authorizedUserId) {
          setToLocalStorage({ key: 'authUserId', value: data.authorizedUserId });
        }

        /* Wrapped in try Catch to avoid crash if the SDK is not loaded successfully */
        try {
          (<any>window).hj('identify', personExternalId, {
            externalId: data.customerId,
            AppVersion: process.env.REACT_APP_VERSION,
          });
          await identifyMixPanelUser(personExternalId);
          trackMixPanel('Logged-in');
        } catch (e) {
          console.error(e);
        }
        setTokenStatus(false);
      },
      onError: (_e: IServerErrorResponse) => {
        const errCode = _e.response.data.httpStatus;
        const errMsg = _e.response.data.errors[0]?.message;
        const errEnum = _e.response.data.errors[0]?.code;
        trackMixPanel('Error Occurred During log in to Web', {
          errorCode: errEnum,
          errorMessage: errMsg,
        });

        if (sentToLoginErrorCodes.includes(errCode)) {
          navigate(`/${VIEWS_MAP.LOGIN}`);
        }
      },
    },
  );
};
